import Filter from 'src/utils/filter'
import PATHS from 'src/router/paths'

import dates from './dates'
import format from './format'
import gam from './gam'

const transform = {
  processObject(item, reportColumnsByKey) {
    return (formatterOrNormalizer) => (subItem) => {
      const isArray = Array.isArray(subItem)
      const getSubItemArray = () => isArray && subItem.map((arrayItem) => {
        const buildItem = () => ((arrayItem?.cf_name && `${arrayItem.cf_name} = ${arrayItem.cf_value}`) || arrayItem)
        return (typeof arrayItem === 'object' && buildItem()) || arrayItem
      })
      const getSubItemObject = () => Object.keys(subItem).reduce((newItem, subItemKey) => {
        const isObject = typeof subItem[subItemKey] === 'object' || ['date', 'dateObject'].indexOf(reportColumnsByKey[subItemKey]?.formatAs) !== -1

        newItem[subItemKey] = (isObject && subItem[subItemKey]) || formatterOrNormalizer(subItem[subItemKey], subItemKey)

        return newItem
      }, subItem)

      return getSubItemArray() || getSubItemObject()
    }
  },
  formatAndNormalize(item, reportColumnsByKey) {
    if (typeof item.normalized !== 'object') item.normalized = {}

    const processObject = this.processObject(item, reportColumnsByKey)

    return (key) => {
      const currentColumn = reportColumnsByKey[key]
      const subItem = item[key]

      if (currentColumn) {
        const formatObject = () => ['date', 'dateObject'].indexOf(currentColumn.formatAs) !== -1
            && typeof subItem === 'object'
            && subItem
            && processObject(format.formatColumn.bind(format, currentColumn))(subItem)
        item[key] = formatObject() || format.formatColumn(currentColumn, item)
      }

      item.normalized[key] = Filter.normalizeValueByType(subItem, key)

      return item
    }
  },
  async freeze(item, reportColumnsByKey) {
    const formatter = this.formatAndNormalize(item, reportColumnsByKey)
    const promises = Object.keys(item).map(async (key) => formatter(key))

    await Promise.all(promises)

    return Object.freeze(item)
  },
  async freezeAll(associationId, columnsByKey, items) {
    const mappedItems = items.map(async (item) => {
      item.associationId = associationId
      item.subId = item.id

      return this.freeze(item, columnsByKey)
    })

    return Promise.all(mappedItems)
  },

  mapCreativeLink(networkCode, creativeCode) {
    return gam.getCreativeUrl(networkCode, creativeCode)
  },

  getOsiView(type, goal, osi) {
    const isApiError = () => osi < 0 && 'N/A'
    const isPreferredDeal = () => type === 'PREFERRED_DEAL' && 'N/A'
    const isSponsorship = () => (type === 'SPONSORSHIP' && goal === 0) && 'N/A'

    return isApiError() || isPreferredDeal() || isSponsorship() || osi
  },
  assignCustomFields(lineItem) {
    const defaultCustomFieldValue = []
    const lineItemCustomFields = lineItem.lineItemCustomFields || defaultCustomFieldValue
    const orderCustomFields = lineItem.orderCustomFields || defaultCustomFieldValue
    const hasCustomFieldForLineItem = !!lineItemCustomFields.length
    const hasCustomFieldForOrder = !!orderCustomFields.length
    const hasCustomFields = hasCustomFieldForLineItem || hasCustomFieldForOrder

    const getCustomFields = () => {
      const combineCustomFields = hasCustomFieldForLineItem && hasCustomFieldForOrder
      const getSetFields = () => (hasCustomFieldForOrder ? orderCustomFields : lineItemCustomFields)
      const getCombinedFields = () => combineCustomFields && orderCustomFields.concat(lineItemCustomFields)

      return hasCustomFields && (getCombinedFields() || getSetFields())
    }

    const customFields = getCustomFields() || defaultCustomFieldValue

    lineItem.hasCustomFields = hasCustomFields
    lineItem.customFields = customFields

    const customFieldsReducer = (customLineItem, customField) => {
      const { cf_name, cf_value } = customField
      customLineItem[cf_name] = cf_value
      return customLineItem
    }

    return customFields.reduce(customFieldsReducer, lineItem)
  },

  pacing(networkCode, columnsByKey, lineItems) {
    const mappedItems = lineItems.map(async (lineItem) => {
      const osi = async () => this.getOsiView(lineItem.type, lineItem.booked, lineItem.osi)
      const lineItemGamLink = async () => gam.getLineItemUrl(networkCode, lineItem.code)
      const orderGamLink = async () => gam.getOrderUrl(networkCode, lineItem.orderCode)
      const customLineItem = transform.assignCustomFields(lineItem)

      customLineItem.allRevenue /= 1000000
      customLineItem.booked = customLineItem.goal
      customLineItem.lineItemCode = customLineItem.code
      customLineItem.lineItemGamLink = await lineItemGamLink()
      customLineItem.networkCode = networkCode
      customLineItem.orderGamLink = await orderGamLink()
      customLineItem.osi = await osi()
      customLineItem.potentialLostRevenue /= 1000000
      customLineItem.priority = customLineItem.gamPriority
      customLineItem.rate /= 1000000

      return transform.freeze(customLineItem, columnsByKey)
    })

    return Promise.all(mappedItems)
  },

  mapLineItemHistory(deliveryReport, pacingReport) {
    return deliveryReport.map((item) => ({
      date: item.date,
      impressions: item.impressions,
      clicks: item.clicks,
      ctr: item.ctr,
      viewableImpressions: item.viewableImpressions,
      viewabilityPercentage: item.viewabilityPercentage,
      osi: pacingReport[item.lineItemId].osi || null,
    }))
  },
  processLineItemHistoryData(allDataForLineItemHistory) {
    const [deliveryItems, pacingItems] = allDataForLineItemHistory
    const invalid = !Array.isArray(deliveryItems) || !Array.isArray(pacingItems)
    const itemsById = pacingItems.reduce((dictionary, pacingItem) => {
      dictionary[pacingItem.lineItemId] = pacingItem
      return dictionary
    }, {})

    return invalid ? [] : this.mapLineItemHistory(deliveryItems, itemsById)
  },

  async discrepancy(networkCode, columnsByKey, discrepancyLineItems) {
    const mappedItems = discrepancyLineItems.map(async (lineItem) => {
      const osi = async () => this.getOsiView(lineItem.type, lineItem.booked, lineItem.osi)
      const lineItemGamLink = async () => gam.getLineItemUrl(networkCode, lineItem.lineItemCode)
      const orderGamLink = async () => gam.getOrderUrl(networkCode, lineItem.orderCode)

      lineItem.booked = lineItem.goal
      lineItem.lineItemGamLink = await lineItemGamLink()
      lineItem.orderGamLink = await orderGamLink()
      lineItem.osi = await osi()
      lineItem.priority = lineItem.gamPriority
      lineItem.rate /= 1000000

      return transform.freeze(lineItem, columnsByKey)
    })

    return Promise.all(mappedItems)
  },

  calculateMetrics(summaryKeys) {
    return (summaryResults, item) => {
      const calculateTotals = (summaryKey) => {
        const summary = summaryResults[summaryKey]
        const { key, total: summaryTotal } = summary
        const { [key]: itemValue } = item.normalized || item

        const total = summaryTotal + itemValue

        summaryResults[summaryKey].items.push(item)
        summaryResults[summaryKey].total = total

        return summaryResults[summaryKey]
      }

      const calculateAverages = (summary) => {
        const { calculation, id, items: { length }, total } = summary

        if (calculation === 'average') {
          summaryResults[id].average = total / length
        }

        return summaryResults[id]
      }

      summaryKeys.map(calculateTotals).map(calculateAverages)

      return summaryResults
    }
  },
  metrics(items, summaries) {
    const summaryKeys = Object.keys(summaries)
    const seed = summaryKeys.reduce((summaryDictionary, summaryKey) => {
      summaryDictionary[summaryKey] = { ...summaries[summaryKey], items: [], total: 0 }
      return summaryDictionary
    }, {})

    if (!items || !items.length) return seed

    return items.reduce(this.calculateMetrics(summaryKeys), seed)
  },

  [PATHS.PROGRAMMATIC.ADX_HEALTH](items, columnsByKey) {
    const mapper = (item) => {
      // @TODO Leverage `transform: 'split'` on inventorySize column.
      item.inventorySize = item.inventorySize.split(',')
      item.adServerLink = gam.getInventoryUrl(item.gamNetworkCode, item.topLevelAdUnitCode)

      return transform.freeze(item, columnsByKey)
    }

    return Promise.all(items.map(mapper))
  },
  [PATHS.PROGRAMMATIC.NETWORK_HEALTH](items, columnsByKey) {
    const mapAdUnit = (item) => {
      // @TODO Leverage `transform` function set on requestedSizes column.
      item.requestedAdSizes = item.requestedAdSizes.split(',')
      item.adServerLink = gam.getInventoryUrl(item.gamNetworkCode, item.topLevelAdUnitCode)

      return transform.freeze(item, columnsByKey)
    }

    return Promise.all(items.map(mapAdUnit))
  },
  [PATHS.PROGRAMMATIC.ADX_AND_OPEN_BIDDING](items, columnsByKey) {
    const mapper = (item) => {
      item.adServerLink = null
      item.week = dates.getWeekOfYear(item.date)

      return transform.freeze(item, columnsByKey)
    }

    return Promise.all(items.map(mapper))
  },

  [PATHS.INTERNAL.TIME_SHEET](entries, summaries) {
    const summaryKeys = Object.keys(summaries)
    const seed = summaryKeys.reduce((summaryDictionary, summaryKey) => {
      summaryDictionary[summaryKey] = { ...summaries[summaryKey], items: [], total: 0 }
      return summaryDictionary
    }, {})

    if (!entries || !entries.length) return seed

    return entries.reduce((original, entry) => {
      const {
        timeClassificationName: timeType,
        duration: durationInMinutes,
      } = entry

      if (!original[timeType]) return original

      const { mtd, total: currentMinutes } = original[timeType]
      const total = currentMinutes + durationInMinutes

      const { timeEntryDate } = entry
      const monthsFromNow = dates.monthsFromNow(timeEntryDate)
      const notFromCurrentMonth = monthsFromNow !== 0

      if (notFromCurrentMonth && mtd) return original

      original[timeType].items.push(entry)
      original[timeType].total = total

      original.TOTAL.items.push(entry)
      original.TOTAL.total += durationInMinutes

      return original
    }, seed)
  },
}

export default transform
